import { useState, useEffect } from 'react';
import './user-order-report.css';
import { reportsAPI } from '../../../config/reports-api';
import { Download, MessageCircle } from 'lucide-react';
import WhatsAppModal from './WhatsAppModal/whatsapp-modal';
import ExcelDownloadModal from './ExcelDownloadModal/excel-download-modal';
import { toast } from 'react-toastify';

const UserOrderReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  const reportCards = [
    {
      key: 'totalUsersCreated',
      label: 'Total Users',
      color: '#06b6d4'
    },
    {
      key: 'totalOrders',
      label: 'Total Orders',
      color: '#3b82f6'
    },
    {
      key: 'deliveredOrders',
      label: 'Delivered Orders',
      color: '#10b981'
    },
    {
      key: 'cancelledOrders',
      label: 'Cancelled Orders',
      percentageKey: 'cancelledOrderPercentage',
      color: '#ef4444'
    },
    {
      key: 'orderedUsers',
      label: 'Ordered Users',
      percentageKey: 'orderdUsersPercentage',
      color: '#8b5cf6'
    },
    {
      key: 'usersWithMultipleOrders',
      label: 'Repeat Customers',
      percentageKey: 'repetitiveUsersPercentage',
      color: '#f59e0b'
    }
  ];

  const newlyJoinedCards = [];

  if (range === 'lifetime') {
    reportCards.push({
      key: 'zeroOrderUsers',
      label: 'Zero Order Users',
      percentageKey: 'zeroOrderUsersPercentage',
      color: '#191919'
    });
  }
  else {
    reportCards.push({
      key: 'usersCreatedButNotOrdered',
      label: 'New Users But Not Ordered',
      percentageKey: 'usersCreatedButNotOrderedPercentage',
      color: '#191919'
    });

    newlyJoinedCards.push({
      key: 'newlyJoinedUsers',
      label: 'Newly Joined Users',
      color: '#0ea5e9'
    });

    newlyJoinedCards.push({
      key: 'newlyJoinedAndOrdered',
      label: 'Newly Joined & Ordered',
      // percentageKey: 'newlyJoinedAndOrderedPercentage',
      color: '#14b8a6'
    });

    newlyJoinedCards.push({
      key: 'newlyJoinedAndHavingCart',
      label: 'Newly Joined & Having Cart',
      // percentageKey: 'newlyJoinedAndHavingCartPercentage',
      color: '#f97316'
    });
  }

  const fetchReportData = async (filterRange) => {
    try {
      setLoading(true);
      setError(null);

      const result = await reportsAPI.getUserOrderReport(
        filterRange,
        filterRange === 'custom' ? customStartDate : null,
        filterRange === 'custom' ? customEndDate : null
      );

      if (result.success) {
        setReportData(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Error fetching report data: ' + err.message);
      console.error('Error fetching report data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData(range);
  }, []);

  const handleRangeChange = (r) => {
    setRange(r);
    if (r !== 'custom') {
      fetchReportData(r);
    }
  };

  const handleLoadReport = () => {
    if (range === 'custom') {
      if (!customStartDate || !customEndDate) {
        toast.error('Please select both start and end dates');
        return;
      }
      if (new Date(customStartDate) > new Date(customEndDate)) {
        toast.error('Start date cannot be after end date');
        return;
      }
    }
    fetchReportData(range);
  };

  const handleRetry = () => {
    fetchReportData(range);
  };

  const handleDownload = (card) => {
    setSelectedCard(card);
    setIsModalOpen(true);
  };

  const handleWhatsApp = (card) => {
    setSelectedCard(card);
    setIsWhatsAppModalOpen(true);
  };

  const getCardValue = (card) => {
    if (!reportData) return 0;
    return reportData[card.key] || 0;
  };

  const getCardPercentage = (card) => {
    if (!reportData) return '';

    let percentage = '';
    if (card.percentageKey) {
      percentage = reportData[card.percentageKey];
    } else {
      const percentageKey = `${card.key}Percentage`;
      percentage = reportData[percentageKey];
    }

    if (percentage === null || percentage === undefined || percentage === '' || isNaN(percentage) || !isFinite(percentage)) {
      return '';
    }

    return Number(percentage).toFixed(0);
  };

  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };

  const renderCard = (card) => (
    <div
      key={card.key}
      className="report-order-stat-card"
      style={{ backgroundColor: card.color }}
    >
      <div className="report-order-card-actions">
        <button
          className="report-order-action-btn report-download-btn"
          onClick={() => handleDownload(card)}
          title="Download Report"
        >
          <Download size={14} />
        </button>
        <button
          className="report-order-action-btn report-whatsapp-btn"
          onClick={() => handleWhatsApp(card)}
          title="Share via WhatsApp"
        >
          <MessageCircle size={14} />
        </button>
      </div>

      <div className="report-order-card-content">
        <div className="report-order-stat-number">
          {formatNumber(getCardValue(card))}
        </div>
        <div className="report-order-stat-label">
          {card.label}
          {getCardPercentage(card) !== '' && (
            <span className="report-percentage"> ({getCardPercentage(card)}%)</span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="report-page">
      <div className="report-page-header">
        <div className="report-page-title">
          <h1>User Order Report</h1>
          <p>Comprehensive overview of user orders and registration analytics</p>
        </div>
      </div>

      <div className="report-range-section">
        <div className="report-range-selector">
          <label>Report Range:</label>
          <select
            value={range}
            onChange={(e) => handleRangeChange(e.target.value)}
            className="report-range-select"
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="7 days">Last 7 Days</option>
            <option value="30 days">Last 30 Days</option>
            <option value="90 days">Last 90 Days</option>
            <option value="lifetime">Lifetime</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>

        {range === 'custom' && (
          <div className="report-custom-date-picker">
            <div className="report-date-input-group">
              <label>Start Date</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="report-date-input"
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="report-date-input-group">
              <label>End Date</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="report-date-input"
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>
        )}

        <button
          className="report-load-btn"
          onClick={handleLoadReport}
        >
          Load Report
        </button>
      </div>

      {loading ? (
        <div className="report-loading-state">
          <div className="report-loading-spinner"></div>
          <p>Loading report data...</p>
        </div>
      ) : error ? (
        <div className="report-error-state">
          <p>Error: {error}</p>
          <button onClick={handleRetry} className="report-retry-button">
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="report-cards-container">
            <div className="report-cards-grid">
              {reportCards.map(renderCard)}
            </div>
          </div>

          {newlyJoinedCards.length > 0 && (
            <div className="report-cards-container newly-joined-container">
              <div className="report-cards-grid">
                {newlyJoinedCards.map(renderCard)}
              </div>
            </div>
          )}
        </>
      )}

      <ExcelDownloadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        cardData={selectedCard}
        reportRange={range}
        customStartDate={customStartDate}
        customEndDate={customEndDate}
      />

      <WhatsAppModal
        isOpen={isWhatsAppModalOpen}
        onClose={() => setIsWhatsAppModalOpen(false)}
        cardData={selectedCard}
        reportRange={range}
        customStartDate={customStartDate}
        customEndDate={customEndDate}
        userCount={selectedCard ? getCardValue(selectedCard) : 0}
      />
    </div>
  );
};

export default UserOrderReport;